import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  DataTable,
  Pagination,
  Select,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  EmptyState,
  Button,
  Icon
} from '@shopify/polaris';
import { ArrowDownIcon } from '@shopify/polaris-icons';
import { manualExportAPI, vendorAPI } from '../utils/api';

function ExportHistory() {
  const [exports, setExports] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [vendorFilter, setVendorFilter] = useState('');

  const fetchExports = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(vendorFilter && { vendorId: vendorFilter })
      };
      const response = await manualExportAPI.getHistory(params);
      setExports(response.data.history || []);
      setPagination(prev => ({ 
        ...prev, 
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error fetching export history:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, vendorFilter]);

  const fetchVendors = useCallback(async () => {
    try {
      const response = await vendorAPI.getAll({ limit: 100 });
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  }, []);

  useEffect(() => {
    fetchExports();
    fetchVendors();
  }, [fetchExports, fetchVendors]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownload = async (exportId, fileName) => {
    try {
      const response = await manualExportAPI.download(exportId);

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. The file may no longer exist.');
    }
  };

  const rows = exports.map((exp) => [
    exp.Vendor?.vendorName || '-',
    exp.exportFileName,
    <Badge>{exp.VendorTemplate?.format?.toUpperCase() || 'XLSX'}</Badge>,
    exp.totalOrders,
    formatDate(exp.exportedAt),
    <Button
      icon={ArrowDownIcon}
      onClick={() => handleDownload(exp.id, exp.exportFileName)}
      accessibilityLabel={`Download ${exp.exportFileName}`}
    >
      Download
    </Button>
  ]);

  return (
    <Page title="Export History" subtitle="View all manual export records">
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="400">
            <Select
              label="Filter by vendor"
              options={[
                { label: 'All Vendors', value: '' },
                ...vendors.map(v => ({ label: v.vendorName, value: v.id }))
              ]}
              value={vendorFilter}
              onChange={setVendorFilter}
            />
          </BlockStack>
        </Card>

        {exports.length === 0 && !loading ? (
          <Card>
            <EmptyState
              heading="No export history found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Export orders using the Manual Export page to see them here.</p>
            </EmptyState>
          </Card>
        ) : (
          <Card>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text']}
              headings={['Vendor', 'File Name', 'Format', 'Total Orders', 'Exported At', 'Actions']}
              rows={rows}
              loading={loading}
            />

            {pagination.totalPages > 1 && (
              <div style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  hasPrevious={pagination.page > 1}
                  onPrevious={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  hasNext={pagination.page < pagination.totalPages}
                  onNext={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                />
              </div>
            )}
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}

export default ExportHistory;
